import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { changeLanguage } from '../i18n';
import { DEMO_GUEST_USER } from '../data/guestDemoData';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('holdsync_token'));
  const [loading, setLoading] = useState(true);
  const [isGuestMode, setIsGuestMode] = useState(false);
  
  // Credit balance state - null means "unknown/loading", number means "confirmed"
  const [creditBalance, setCreditBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  // Earned this week from referrals - null means "unavailable", number means "confirmed"
  const [earnedThisWeek, setEarnedThisWeek] = useState(null);

  // Setup axios interceptor for 401 errors
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          // Token is invalid or expired
          localStorage.removeItem('holdsync_token');
          setToken(null);
          setUser(null);
          setCreditBalance(null);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          // Set initial balance from auth response
          setCreditBalance(response.data.creditBalance ?? null);
          // Sync language from backend if user has a preference saved
          if (response.data.language) {
            changeLanguage(response.data.language);
          }
        } catch (error) {
          console.error('Auth init error:', error);
          localStorage.removeItem('holdsync_token');
          setToken(null);
          setCreditBalance(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('holdsync_token', newToken);
    setToken(newToken);
    setUser(userData);
    setIsGuestMode(false);
    // Backend-confirmed balance from login response
    setCreditBalance(userData.creditBalance ?? null);
    return userData;
  };

  const register = async (email, password, fullName, username, city, state, referralCode = null, phoneNumber = null, website = null) => {
    const payload = { email, password, fullName, username, city, state };
    if (referralCode) {
      payload.referralCode = referralCode;
    }
    if (phoneNumber) {
      payload.phoneNumber = phoneNumber;
    }
    if (website) {
      payload.website = website;
    }
    const response = await axios.post(`${API}/auth/register`, payload);
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('holdsync_token', newToken);
    setToken(newToken);
    setUser(userData);
    setIsGuestMode(false);
    // Backend-confirmed balance from register response
    setCreditBalance(userData.creditBalance ?? null);
    setEarnedThisWeek(null); // Will be populated on next refresh
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('holdsync_token');
    setToken(null);
    setUser(null);
    setCreditBalance(null);
    setEarnedThisWeek(null);
    setIsGuestMode(false);
  };

  /**
   * Enter guest preview mode with demo data.
   * No database writes, no persistent state.
   */
  const enterGuestMode = () => {
    // Clear any existing auth state
    localStorage.removeItem('holdsync_token');
    setToken(null);
    // Set guest user (demo data, read-only)
    setUser(DEMO_GUEST_USER);
    setIsGuestMode(true);
    setCreditBalance(0);
    setEarnedThisWeek(0);
    setLoading(false);
  };

  /**
   * Exit guest mode and return to unauthenticated state
   */
  const exitGuestMode = () => {
    setUser(null);
    setIsGuestMode(false);
    setCreditBalance(null);
    setEarnedThisWeek(null);
  };

  const getAuthHeaders = () => {
    // Always get fresh token from localStorage to avoid stale closures
    const currentToken = localStorage.getItem('holdsync_token') || token;
    return {
      Authorization: `Bearer ${currentToken}`
    };
  };

  /**
   * Refresh credit balance and weekly earned from backend.
   * This is the ONLY way to update balance - always fetches from server.
   * Returns the full credit data, or null if fetch failed.
   * On failure, sets balance to null (hide rather than guess).
   */
  const refreshCreditBalance = useCallback(async () => {
    if (!token) {
      setCreditBalance(null);
      setEarnedThisWeek(null);
      return null;
    }
    
    setBalanceLoading(true);
    try {
      const response = await axios.get(`${API}/credits/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { balance, earned_last_7_days } = response.data;
      setCreditBalance(balance);
      // Only set earned if present (server may omit if unavailable)
      setEarnedThisWeek(earned_last_7_days !== undefined ? earned_last_7_days : null);
      return { balance, earnedThisWeek: earned_last_7_days };
    } catch (error) {
      console.error('Failed to refresh credits:', error);
      // On error, set to null - hide rather than show stale/guessed value
      setCreditBalance(null);
      setEarnedThisWeek(null);
      return null;
    } finally {
      setBalanceLoading(false);
    }
  }, [token]);

  /**
   * Check if we have a confirmed (non-null) balance
   */
  const hasConfirmedBalance = creditBalance !== null;

  return (
    <AuthContext.Provider value={{ 
      user, 
      setUser,
      token, 
      loading, 
      login, 
      register, 
      logout, 
      getAuthHeaders,
      isAuthenticated: !!user && !isGuestMode,
      // Guest mode state
      isGuestMode,
      enterGuestMode,
      exitGuestMode,
      // Credit balance - null means unknown/unavailable
      creditBalance,
      balanceLoading,
      hasConfirmedBalance,
      refreshCreditBalance,
      // Earned this week from referrals - null means unavailable
      earnedThisWeek
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
